// Список проектов и аналитика по исполнителям.
// Эндпоинты: GET /projects/list, GET /projects/analytics.
// Типы соответствуют tasks_module_internal_models.* из swagger.
import { http } from '@/shared/api/http';
import type { DeepRequired, Schema, WithOptional } from '@/shared/api';
import type {
  ProjectAggregates,
  ProjectCardUser,
  ProjectTimeStats,
} from './project-card';

// --- Отдел в строке списка (для колец по отделам на карточке) ---
export type ProjectListDeptStat = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectListDeptStat'>
>;

// --- Поля из models.Project, нужные на табе «Данные» ---
export interface ProjectContact {
  id?: number;
  name?: string;
  workPosition?: string;
  phone?: string;
}

export interface ProjectCompany {
  id?: number;
  title?: string;
}

export type ProjectCustomField = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.CustomField'>>,
  'name' | 'label' | 'type' | 'value'
>;

// --- Элемент списка проектов (models.Project, обогащённый BE-2) ---
export interface ProjectListItem {
  id: number;
  entityTypeId: number;
  title: string;
  stageId?: string;
  stageName?: string;
  groupId?: number;
  assignedById?: string;
  createdTime?: string;
  dateStart?: string;
  dateFinish?: string;
  bitrixUrl?: string;
  lastMessage?: string;
  resume?: string;
  company?: ProjectCompany;
  contacts?: ProjectContact[];
  customFields?: ProjectCustomField[];
  // BE-2: поля обогащения для карточки.
  deadline?: string;
  completionPercent?: number;
  teamCount?: number;
  teamLead?: ProjectCardUser;
  timeStats?: ProjectTimeStats;
  aggregates?: ProjectAggregates;
  departments?: ProjectListDeptStat[];
  // Паритет с карточкой (projects-dashboard-parity-backend-spec.md):
  // progressPercent — выполнение (доля закрытых задач), successPercent — реальная
  // успеваемость (доля задач в срок), completionPercent — утилизация бюджета.
  // Три РАЗНЫХ числа; раньше success был копией completion.
  progressPercent?: number;
  successPercent?: number;
  helpCount?: number;
  timeExtensionSeconds?: number;
  // Готовые флаги и цвет здоровья — фронт их больше не выводит сам.
  overdue?: boolean;
  overspend?: boolean;
  health?: 'ok' | 'warn' | 'risk';
  // slaAvgSeconds/alertCount по-прежнему НЕ приходят — у бэка нет продуктовой
  // формулы (SLA/Alerts были заглушками с самого начала). Карточка их не рисует.
  // BE-1.2: семантика стадии из Bitrix (process/success/fail) — заменяет regex по имени.
  stageSemantics?: 'process' | 'success' | 'fail' | '';
  // Дата перехода на завершённый этап. Если задана — срок фиксируется на этот
  // момент (см. projectDaysLeft), иначе счётчик дней продолжал бы бежать у
  // закрытых проектов. Есть в models.Project, как и в summary карточки.
  movedAt?: string | null;
  // BE-2 (сессия 2): пауза срока в статусной машине.
  awaitingSince?: string;
  pausedSeconds?: number;
  // BE-4: время последней активности проекта (для «обновлено N назад» в Пульсе).
  updatedAt?: string;
}

export type ProjectListMeta = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectListMeta'>
>;

// result берём в своей форме: в спеке stageSemantics описан как string, а фронт
// сузил его до '' | 'success' | 'process' | 'fail' (по нему красятся кольца).
export type ProjectListResponse = Omit<
  WithOptional<
    DeepRequired<Schema<'tasks_module_internal_models.ProjectListResponse'>>,
    'meta'
  >,
  'result'
> & { result: ProjectListItem[] };

// --- Аналитика по исполнителям (для двух верхних диаграмм) ---
export type AssigneeAnalytics = DeepRequired<
  Schema<'tasks_module_internal_models.AssigneeAnalytics'>
>;

export type ProjectAnalyticsResponse = DeepRequired<
  Schema<'tasks_module_internal_models.ProjectAnalyticsResponse'>
>;

// --- Параметры фильтрации (общие для list и analytics) ---
export interface ProjectsFilterParams {
  entityTypeId?: number;
  stageId?: string;
  stageName?: string;
  search?: string;
  responsibleId?: number;
  dateFinishFrom?: string;
  dateFinishTo?: string;
  groupId?: number;
  // BE-4: серверный мультивыбор отделов (CSV) — OR по исполнителям задач.
  departmentIds?: string;
  // BE-4: серверные тумблеры (счётчики всегда в meta, независимо от них).
  overdue?: boolean;
  overspend?: boolean;
  onlyMine?: boolean;
  status?: 'active' | 'inactive' | 'completed' | 'all';
  // BE §6: расширено на все колонки реестра «Справочник» (было только
  // deadline/title) — teamLeadName/declaredSeconds/spentSeconds/totalCost/
  // successPercent сортируют сервер, клиентская пересортировка не нужна.
  sortBy?:
    | 'deadline'
    | 'title'
    | 'teamLeadName'
    | 'declaredSeconds'
    | 'spentSeconds'
    | 'totalCost'
    | 'successPercent';
  sortOrder?: 'asc' | 'desc';
}

export interface ProjectsListParams extends ProjectsFilterParams {
  page?: number;
  perPage?: number;
}

// --- API ---
export const fetchProjectsList = async (
  params?: ProjectsListParams
): Promise<ProjectListResponse> => {
  const { data } = await http.get('/projects/list', { params });
  return data;
};

// Те же фильтры, что у /projects/list (без page/perPage) — считается по всему
// отфильтрованному набору, поэтому список и диаграммы синхронны.
export const fetchProjectsAnalytics = async (
  params?: ProjectsFilterParams
): Promise<ProjectAnalyticsResponse> => {
  const { data } = await http.get('/projects/analytics', { params });
  return data;
};

// --- Стадии воронки (BE-1.2 + parity §3.2): порядок, семантика и цвет с бэка.
export interface ProjectStage {
  stageId: string;
  name: string;
  semantics: 'process' | 'success' | 'fail' | '';
  sort: number;
  /**
   * Цвет стадии («#2FC6F6»). Колонки COLOR в `b_crm_status` есть не во всех
   * версиях коробки: бэк проверяет её в рантайме и отдаёт пустую строку, если
   * колонки нет. Тогда работает наша палитра (см. funnelColumns).
   */
  color?: string;
  /** Появился вместе с мульти-типовым запросом — к какому смарт-процессу стадия. */
  entityTypeId?: number;
  /**
   * Воронка (канбан) внутри смарт-процесса — у одного entityTypeId их может
   * быть несколько, sort нумеруется ВНУТРИ воронки (см. buildFunnelColumns).
   */
  categoryId?: number;
  /** Пустая, если бэк не смог прочитать справочник воронок — есть фолбэк по categoryId. */
  categoryName?: string;
}

/**
 * Стадии портала. Без аргумента — все типы разом; можно сузить одним типом или
 * списком (parity §3.2). Ответ уже отсортирован бэком по SORT.
 */
export const fetchProjectStages = async (
  entityTypeIds?: number[]
): Promise<{ result: ProjectStage[] }> => {
  const params =
    entityTypeIds && entityTypeIds.length === 1
      ? { entityTypeId: entityTypeIds[0] }
      : entityTypeIds && entityTypeIds.length > 1
        ? { entityTypeIds: entityTypeIds.join(',') }
        : undefined;
  const { data } = await http.get('/projects/stages', { params });
  return data;
};

// --- Статусная машина проекта (BE-2) ---
export type ProjectStateAction = 'start' | 'awaitInfo' | 'resume' | 'complete';

export const postProjectState = async (
  entityTypeId: number,
  id: number,
  action: ProjectStateAction
): Promise<ProjectListItem> => {
  const { data } = await http.post(`/projects/${entityTypeId}/${id}/state`, {
    action,
  });
  return data;
};

export const postProjectTeamLead = async (
  entityTypeId: number,
  id: number,
  userId: number
): Promise<ProjectListItem> => {
  const { data } = await http.post(
    `/projects/${entityTypeId}/${id}/team-lead`,
    { userId }
  );
  return data;
};

export const postProjectRequestStatus = async (
  entityTypeId: number,
  id: number
): Promise<void> => {
  await http.post(`/projects/${entityTypeId}/${id}/request-status`);
};

// --- Инлайн-правка полей проекта из реестра «Справочник» ---
// PATCH /projects/{entityTypeId}/{id} — на бэке реализован (models.ProjectPatchInput):
// название (title), срок (deadline) и план-часы (declaredSeconds).
export type ProjectPatch = WithOptional<
  DeepRequired<Schema<'tasks_module_internal_models.ProjectPatchInput'>>,
  'title' | 'deadline' | 'declaredSeconds'
>;

export const patchProject = async (
  entityTypeId: number,
  id: number,
  patch: ProjectPatch
): Promise<ProjectListItem> => {
  const { data } = await http.patch(`/projects/${entityTypeId}/${id}`, patch);
  return data;
};

// --- Массовые действия над проектами (POST /projects/bulk-action) ---
// Проект адресуется ПАРОЙ entityTypeId+id: id уникален только внутри своего
// типа смарт-процесса — это единственное отличие контракта от задачного
// /tasks/bulk-action.
//
// Действия «поставить стадию» нет намеренно: STAGE_ID имеет вид
// DT{entityTypeId}_{categoryId}:{STATUS_ID}, то есть привязан и к типу, и к
// воронке, и одно значение нельзя применить к разнотипной пачке. Массовая
// смена статуса идёт через action=state — целевая стадия резолвится для
// каждого проекта своя. Сырая стадия — по-прежнему одиночная ручка /field.
export type ProjectKey = Schema<'tasks_module_internal_models.ProjectKey'>;

export type BulkProjectActionRequest = { projects: ProjectKey[] } & (
  | { action: 'deadline'; deadline: string }
  | { action: 'state'; state: ProjectStateAction }
  | { action: 'teamLead'; teamLeadId: number }
);

/** Отказ по бизнес-правилу — НЕ ошибка. Сейчас случай один: завершить проект
 *  с незакрытыми задачами нельзя, их число лежит в openTasksCount. */
export type BulkProjectSkip =
  Schema<'tasks_module_internal_models.BulkProjectSkip'>;

/** Настоящий сбой: проект не найден, переход не настроен, ошибка Битрикса. */
export type BulkProjectError =
  Schema<'tasks_module_internal_models.BulkProjectError'>;

export type BulkProjectActionResponse =
  Schema<'tasks_module_internal_models.BulkProjectActionResponse'>;

/** Запрос всегда отдаёт 200 и разложенный результат — один упавший проект не
 *  мешает остальным. Проверять надо все ТРИ списка, а не только HTTP-код. */
export const bulkProjectAction = async (
  payload: BulkProjectActionRequest
): Promise<BulkProjectActionResponse> => {
  const { data } = await http.post('/projects/bulk-action', payload);
  return data;
};

// --- Схема полей карточки проекта (GET /projects/fields) ---
// customFields[] в карточке и реестре — это ЗНАЧЕНИЯ конкретного проекта, и
// только непустые: незаполненное поле там не видно, хотя ручка правки его
// принимает. Эта ручка отдаёт СХЕМУ — какие поля вообще бывают у карточек
// типа, включая пустые, и варианты выбора для enumeration.
export type ProjectFieldSchema =
  Schema<'tasks_module_internal_models.ProjectFieldSchema'>;

export type ProjectFieldOption =
  Schema<'tasks_module_internal_models.FieldOption'>;

/** Варианты для STAGE_ID сюда намеренно не попадают — стадии зависят ещё и от
 *  воронки, их берём из fetchProjectStages. */
export const fetchProjectFields = async (
  entityTypeId: number
): Promise<{ entityTypeId: number; fields: ProjectFieldSchema[] }> => {
  const { data } = await http.get('/projects/fields', {
    params: { entityTypeId },
  });
  return data;
};

/** Сохранить одно поле. Для enumeration отправляется id варианта (не текст),
 *  null очищает поле. Значение по типу бэк не валидирует — неверный формат
 *  вернётся ошибкой Битрикса. Каждая правка пишется в историю проекта (чат).
 *  Файлы — отдельной ручкой uploadSmartFieldFile (multipart). */
export const postProjectField = async (
  entityTypeId: number,
  id: number,
  field: string,
  value: unknown
): Promise<void> => {
  await http.post(`/projects/${entityTypeId}/${id}/field`, { field, value });
};
